import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Settings,
  AccountBalance,
  SportsEsports,
  ExitToApp,
  Notifications,
  ChevronLeft,
  TrendingUp,
  BugReport,
  AccountBalanceWallet,
  Campaign,
  Assignment,
  WorkspacePremium,
  CurrencyBitcoin,
  Casino,
  Timeline,
  Whatshot,
  Analytics,
  Search,
  SmartToy,
  Handshake,
  ConfirmationNumber
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 240;

const menuItems = [
  { text: 'Дашборд', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Активность Live', icon: <Timeline />, path: '/activity-live' },
  { text: 'Пользователи', icon: <People />, path: '/users' },
  { text: 'Задания', icon: <Assignment />, path: '/tasks' },
  { text: 'Стейкинги', icon: <AccountBalance />, path: '/stakings' },
  { text: 'Burn Pool', icon: <Whatshot />, path: '/burn-pool' },
  { text: 'Market Maker', icon: <TrendingUp />, path: '/market-maker' },
  { text: 'Сканер кошельков', icon: <Search />, path: '/wallet-scanner' },
  { text: 'Мини-игры', icon: <SportsEsports />, path: '/minigames' },
  { text: 'SLOTS', icon: <Casino />, path: '/slots-settings' },
  { text: 'Лояльность слотов', icon: <Casino />, path: '/loyalty' },
  { text: 'Выводы', icon: <AccountBalanceWallet />, path: '/withdrawals' },
  { text: 'TON Интеграция', icon: <CurrencyBitcoin />, path: '/ton-integration' },
  { text: 'Token Analytics', icon: <Analytics />, path: '/token-analytics' },
  { text: 'PUSH алерты', icon: <Campaign />, path: '/push-alerts' },
  { text: 'Jarvis AI', icon: <SmartToy />, path: '/jarvis' },
  { text: 'Промокоды', icon: <ConfirmationNumber />, path: '/promo' },
  { text: 'Партнёры CPA', icon: <Handshake />, path: '/partners' },
  { text: 'Настройки', icon: <Settings />, path: '/settings' },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // §UX (владелец 20.07): при F5 возвращать на ту же позицию скролла страницы.
  // Скроллится ОКНО (main = overflow:hidden, minHeight:100vh). Отключаем нативное
  // восстановление — делаем своё, per-путь, с ретраями (контент грузится асинхронно,
  // высота дорастает не сразу). Активная подвкладка отдельно хранится в usePersistedTab.
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);
  useEffect(() => {
    const skey = `adm_scroll_${location.pathname}`;
    // сохранение позиции (троттл 200мс)
    let saveT = null;
    const onScroll = () => {
      if (saveT) return;
      saveT = setTimeout(() => {
        saveT = null;
        try { sessionStorage.setItem(skey, String(Math.round(window.scrollY))); } catch { /* квота */ }
      }, 200);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // восстановление: ждём, пока страница дорастёт до сохранённой высоты (или таймаут ~2с)
    let want = 0;
    try { want = Number(sessionStorage.getItem(skey)) || 0; } catch { want = 0; }
    let restoreId = null;
    if (want > 0) {
      let tries = 0;
      restoreId = setInterval(() => {
        tries += 1;
        const maxY = document.documentElement.scrollHeight - window.innerHeight;
        if (maxY >= want || tries >= 20) {
          window.scrollTo(0, want);
          clearInterval(restoreId);
          restoreId = null;
        }
      }, 100);
    }
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (saveT) clearTimeout(saveT);
      if (restoreId) clearInterval(restoreId);
    };
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: 64
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <TrendingUp sx={{ mr: 1, fontSize: 32 }} />
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold' }}>
            TON MOON
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(99, 102, 241, 0.2)',
                  borderLeft: '3px solid #6366f1',
                },
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                }
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? '#6366f1' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#1e293b',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: { xs: '0.9rem', md: '1.25rem' } }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Админ панель'}
          </Typography>

          <Tooltip title="Уведомления">
            <IconButton color="inherit" sx={{ mr: 2 }}>
              <Badge badgeContent={0} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
              {user?.username || 'Администратор'}
            </Typography>
            <IconButton onClick={handleMenuClick} sx={{ p: 0 }}>
              <Avatar sx={{ bgcolor: '#6366f1' }}>
                {(user?.username || 'A')[0].toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1 }} /> Выход
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: '#1e293b'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: '#1e293b',
              borderRight: '1px solid rgba(255, 255, 255, 0.1)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          // без minWidth:0 широкая таблица внутри flex-строки распирает
          // контейнер, и на телефоне вся страница уезжает вбок
          minWidth: 0,
          maxWidth: '100%',
          minHeight: '100vh',
          backgroundColor: '#0f172a',
          mt: 8,
          overflowX: 'hidden'
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;